"use client";

import { useState, useSyncExternalStore } from "react";
import AutoScroll from "embla-carousel-auto-scroll";
import {
  Award,
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  CheckSquare,
  Clock,
  CreditCard,
  Dumbbell,
  FileText,
  Flame,
  Heart,
  Home,
  Mail,
  MessageCircle,
  MessageSquare,
  Mic,
  Music,
  Pause,
  Phone,
  PiggyBank,
  Play,
  Receipt,
  RefreshCw,
  Repeat,
  Scale,
  ScanLine,
  Send,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Target,
  TrendingUp,
  Users,
  Utensils,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SectionHeader } from "@/components/ui/section-header";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

type PillItem =
  | {
      type: "label";
      icon: LucideIcon;
      text: string;
      color: string;
    }
  | { type: "pill"; icon: LucideIcon; text: string; highlight?: boolean };

const ROWS: PillItem[][] = [
  // Row 1
  [
    { type: "label", icon: Flame, text: "Creators", color: "text-pink-500" },
    {
      type: "pill",
      icon: MessageCircle,
      text: "Draft replies to DMs you paste in, in your voice",
      highlight: true,
    },
    {
      type: "pill",
      icon: Repeat,
      text: "Win-back messages for expired subscribers, ready for your approval",
    },
    { type: "label", icon: Scale, text: "Law Firms", color: "text-blue-500" },
    {
      type: "pill",
      icon: Clock,
      text: "Track every billable minute automatically",
      highlight: true,
    },
    {
      type: "pill",
      icon: FileText,
      text: "Draft intake memos from phone consultations",
    },
    {
      type: "pill",
      icon: Calendar,
      text: "Court deadlines tracked with automatic reminders",
    },
    { type: "label", icon: Target, text: "Sales", color: "text-amber-500" },
    {
      type: "pill",
      icon: Zap,
      text: "Hot leads get instant Slack alerts to your phone",
    },
    {
      type: "label",
      icon: Home,
      text: "Real Estate",
      color: "text-cyan-500",
    },
    {
      type: "pill",
      icon: Phone,
      text: "Draft a reply to every Zillow lead the moment it comes in",
      highlight: true,
    },
    {
      type: "pill",
      icon: Camera,
      text: "Listing descriptions written from property photos",
    },
  ],
  // Row 2
  [
    {
      type: "label",
      icon: ShoppingCart,
      text: "E-Commerce",
      color: "text-violet-500",
    },
    {
      type: "pill",
      icon: ShoppingCart,
      text: "Abandoned cart emails that actually convert",
      highlight: true,
    },
    {
      type: "pill",
      icon: TrendingUp,
      text: "Upsell emails based on what they already bought",
    },
    {
      type: "pill",
      icon: RefreshCw,
      text: "“Time to reorder” reminders for consumables",
    },
    { type: "label", icon: Heart, text: "Weddings", color: "text-pink-500" },
    {
      type: "pill",
      icon: CheckSquare,
      text: "Guest RSVPs tracked & seating auto-arranged",
    },
    {
      type: "label",
      icon: Users,
      text: "Personal",
      color: "text-orange-500",
    },
    {
      type: "pill",
      icon: ShoppingBag,
      text: "Grocery lists auto-built from your meal plan",
    },
    {
      type: "pill",
      icon: PiggyBank,
      text: "Bill due dates tracked with alerts before they’re late",
    },
    {
      type: "label",
      icon: Utensils,
      text: "Restaurants",
      color: "text-red-500",
    },
    {
      type: "pill",
      icon: Phone,
      text: "Draft replies to reservation emails, ready for your approval",
      highlight: true,
    },
    {
      type: "pill",
      icon: Star,
      text: "Ask every diner for a Google review next morning",
    },
  ],
  // Row 3
  [
    {
      type: "label",
      icon: Dumbbell,
      text: "Fitness",
      color: "text-emerald-400",
    },
    {
      type: "pill",
      icon: CreditCard,
      text: "Failed payment follow-ups before they churn",
    },
    {
      type: "pill",
      icon: Award,
      text: "Milestone celebrations: 100th class, 1-year anniversary",
    },
    {
      type: "label",
      icon: Briefcase,
      text: "Auto Dealers",
      color: "text-blue-500",
    },
    {
      type: "pill",
      icon: Phone,
      text: "Every lead gets a drafted reply as soon as you approve it",
      highlight: true,
    },
    {
      type: "pill",
      icon: MessageSquare,
      text: 'Draft replies to "Is this still available?" messages for you to approve',
    },
    {
      type: "pill",
      icon: Mail,
      text: "Service reminders at 5K, 10K, 15K miles",
    },
    { type: "label", icon: Flame, text: "Creators", color: "text-pink-500" },
    {
      type: "pill",
      icon: Target,
      text: "Promote new drops to segmented fan lists",
      highlight: true,
    },
    {
      type: "pill",
      icon: Smartphone,
      text: "Daily earnings dashboard delivered to your phone",
    },
    {
      type: "label",
      icon: Receipt,
      text: "Accounting",
      color: "text-amber-500",
    },
    {
      type: "pill",
      icon: ScanLine,
      text: 'Paste a receipt into chat and your agent reads it',
      highlight: true,
    },
    {
      type: "pill",
      icon: Wallet,
      text: "Invoice follow-ups on day 7, 14, and 30",
    },
  ],
  // Row 4
  [
    {
      type: "label",
      icon: Music,
      text: "Musicians",
      color: "text-violet-500",
    },
    {
      type: "pill",
      icon: Mic,
      text: "AI responds to booking inquiries with your rates",
      highlight: true,
    },
    {
      type: "pill",
      icon: Music,
      text: "Playlist submission outreach drafted for your approval",
    },
    {
      type: "pill",
      icon: MessageCircle,
      text: "Draft replies to fan messages you paste in",
    },
    {
      type: "label",
      icon: Building2,
      text: "Agencies",
      color: "text-emerald-500",
    },
    {
      type: "pill",
      icon: BarChart3,
      text: "Client reports generated without touching a spreadsheet",
      highlight: true,
    },
    {
      type: "pill",
      icon: Send,
      text: "Invoice drafts ready for review on the 1st of every month",
    },
    { type: "label", icon: Target, text: "Sales", color: "text-amber-500" },
    {
      type: "pill",
      icon: FileText,
      text: "Proposals auto-generated from discovery call transcripts",
    },
  ],
];

function Pill({ item }: { item: PillItem }) {
  const Icon = item.icon;
  if (item.type === "label") {
    return (
      <span
        className={cn(
          "flex h-full shrink-0 items-center gap-1.5 px-1.5 text-[0.7rem] font-black tracking-[0.1em] uppercase",
          item.color,
        )}
      >
        <Icon size={13} aria-hidden />
        {item.text}
      </span>
    );
  }

  return (
    <span className="use-case-pill relative isolate flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[0.8rem] font-semibold whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5">
      <Icon
        size={13}
        className={cn(item.highlight && "text-rl-accent")}
        aria-hidden
      />
      {item.text}
    </span>
  );
}

function MarqueeRow({
  items,
  direction,
  speed,
  paused,
}: {
  items: PillItem[];
  direction: "forward" | "backward";
  speed: number;
  paused: boolean;
}) {
  const doubled = [...items, ...items];

  return (
    <div>
      <Carousel
        className="use-case-carousel"
        opts={{
          loop: true,
          align: "start",
          dragFree: true,
          containScroll: false,
        }}
        plugins={
          paused
            ? []
            : [
                AutoScroll({
                  speed,
                  direction,
                  stopOnInteraction: false,
                  stopOnMouseEnter: true,
                  stopOnFocusIn: true,
                  playOnInit: true,
                }),
              ]
        }
      >
        <CarouselContent className="-ml-2.5">
          {doubled.map((item, i) => (
            <CarouselItem key={`${item.text}-${i}`} className="basis-auto pl-2.5">
              <Pill item={item} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}

const ROW_CONFIG: { direction: "forward" | "backward"; speed: number }[] = [
  { direction: "forward", speed: 0.5 },
  { direction: "backward", speed: 0.4 },
  { direction: "forward", speed: 0.45 },
  { direction: "backward", speed: 0.5 },
];

// Honor prefers-reduced-motion. The media query is a browser-only external
// store — useSyncExternalStore hydrates cleanly via the server snapshot
// (motion enabled) and flips on the client without an extra mount-effect
// render.
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
function subscribeToReducedMotion(onChange: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
const getReducedMotionSnapshot = () =>
  window.matchMedia(REDUCED_MOTION_QUERY).matches;
const getReducedMotionServerSnapshot = () => false;

export function UseCases() {
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const paused = reducedMotion || manuallyPaused;

  return (
    <section id="usecases" className="bg-rl-bg2 py-24 md:py-28">
      <div className="mx-auto max-w-280 px-6 md:px-8">
        <SectionHeader
          tag="Use Cases"
          heading="What Your AI Agents Can Do"
          subheading="Every business. Every workflow. If it's repetitive, your AI handles it."
        />
        <div className="mt-4 flex justify-center md:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={
              manuallyPaused
                ? "Play the scrolling use-case examples"
                : "Pause the scrolling use-case examples"
            }
            onClick={() => setManuallyPaused((prev) => !prev)}
          >
            {manuallyPaused ? (
              <Play aria-hidden="true" />
            ) : (
              <Pause aria-hidden="true" />
            )}
            {manuallyPaused ? "Play" : "Pause"}
          </Button>
        </div>
      </div>

      <div className="use-case-marquee relative">
        <div className="from-rl-bg2 pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r to-transparent sm:w-40" />
        <div className="from-rl-bg2 pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l to-transparent sm:w-40" />

        <div className="flex flex-col gap-3">
          {ROWS.map((row, i) => {
            const cfg = ROW_CONFIG[i] ?? {
              direction: "forward" as const,
              speed: 0.5,
            };
            return (
              <MarqueeRow
                key={`row-${row[0]?.text ?? i}`}
                items={row}
                direction={cfg.direction}
                speed={cfg.speed}
                paused={paused}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
